import styled from 'styled-components'
import get from 'lodash/get'
import isObject from 'lodash/isObject'
import breakpoint from 'components/breakpoint'
import config from 'config'

const getErrorBorder = props => props.error && props.theme.input.errorBorder
const getErrorBorderRight = props => {
  if (props.error) {
    return props.errorBorderRight || props.theme.input.errorBorderRight || props.theme.input.errorBorder
  }
}
const getErrorBorderBottom = props => {
  if (props.error) {
    return props.theme.input.errorBorderBottom || props.theme.input.errorBorder
  }
}

const getFocusBorder = props => getErrorBorder(props) || props.theme.input.focusBorder
const getFocusBorderRight = props =>
  getErrorBorderRight(props) || props.focusBorderRight || props.theme.input.focusBorderRight
const getFocusBorderBottom = props => getErrorBorderBottom(props) || props.theme.input.focusBorderBottom

const getHoverBorder = props => getErrorBorder(props) || props.theme.input.hoverBorder
const getHoverBorderRight = props =>
  getErrorBorderRight(props) || props.hoverBorderRight || props.theme.input.hoverBorderRight
const getHoverBorderBottom = props => getErrorBorderBottom(props) || props.theme.input.hoverBorderBottom

const getBorder = props => {
  if (props.focused) {
    return getFocusBorder(props)
  }
  return getErrorBorder(props) || props.theme.input.border
}

const getBorderRight = props => {
  if (props.focused) {
    return getFocusBorderRight(props)
  }

  return getErrorBorderRight(props) || props.borderRight || props.theme.input.borderRight
}

const getBorderBottom = props => {
  if (props.focused) {
    return getFocusBorderBottom(props)
  }

  return getErrorBorderBottom(props) || props.theme.input.borderBottom
}

const getErrorBackground = props => props.error && props.theme.input.errorBackground
const getBackground = props => getErrorBackground(props) || props.backgroundColor || props.theme.input.background

const isCheckIconVisible = props => get(props.theme, 'select.menu.isCheckIconVisible', true)
const getCheckIcon = props => props.theme.select.menu.checkIcon || 'NB-check-form.svg'

const getFontSize = (props, breakpoint) => {
  const inputFontSize = props.theme.input.fontSize
  if (isObject(inputFontSize)) {
    return inputFontSize[breakpoint]
  }
  return inputFontSize || props.theme.font.mobile.size
}
const getFontFamily = props => props.theme.select.fontFamily || props.theme.font.family
const getLineHeight = props => props.theme.font.mobile.height
const getHeight = props => props.theme.input.height
const getBorderRadius = props => props.theme.select.borderRadius || props.theme.input.borderRadius || 0
const getBorderRadiusOnOpen = props => get(props.theme, 'select.open.borderRadius')
const getPadding = props => get(props.theme, 'input.padding', '9px 0 9px 16px')

const getMenuBorder = props => props.theme.select.menu.border
const getMenuColor = props => get(props.theme, 'select.menu.color', 'inherit')
const getMenuBorderRadius = props => get(props.theme, 'select.menu.borderRadius', '0px')
const getMenuPadding = props => get(props.theme, 'select.menu.padding', '0px')
const getMenuBorderTop = props => get(props.theme, 'select.menu.borderTop', 0)
const getMenuMarginTop = props => get(props.theme, 'select.menu.marginTop', 0)
const getMenuListBorderRadius = props => get(props.theme, 'select.menu.list.borderRadius', '0px')

const getMenuListBackgroundColor = props => get(props.theme, 'select.menu.list.backgroundColor', '#FFFFFF')
const getMenuListBoxShadow = props => get(props.theme, 'select.menu.list.boxShadow', '0 2px 4px 0 rgba(0, 0, 0, 0.5)')

const getOptionPadding = props => get(props.theme, 'select.menu.option.padding', '4px 16px 4px 43px')
const getOptionColor = props => get(props.theme, 'select.menu.option.color', 'inherit')
const getOptionFontFamily = props => get(props.theme, 'select.menu.option.fontFamily', 'inherit')

const getFocusedOptionBackground = props => props.theme.select.menu.option.focusBackground
const getFocusedOptionBorder = props => props.theme.select.menu.option.focusBorder
const getFocusedOptionColor = props => props.theme.select.menu.option.focusColor

const getSelectedOptionFontFamily = props => props.theme.select.menu.option.selectedFontFamily

const getPlaceholderColor = props => get(props.theme, 'select.menu.placeholder.color')
const getSingleValueColor = props => get(props.theme, 'select.menu.singleValue.color')
const getDropdownIndicatorPadding = props => get(props.theme, 'select.dropdownIndicator.padding', '8px')

const InputSelectView = styled.div`
  & .select__control {
    box-sizing: border-box;
    width: 100%;
    font-size: ${props => getFontSize(props, 'mobile')};
    font-family: ${getFontFamily};
    height: ${getHeight};
    appearance: none;
    border-radius: ${getBorderRadius};
    background-color: ${getBackground};
    padding: ${getPadding};
    flex-wrap: nowrap;
    border: ${getBorder};
    border-right: ${getBorderRight};
    border-bottom: ${getBorderBottom};

    ${breakpoint('portrait')`
      font-size: ${props => getFontSize(props, 'portrait')};
    `};
  }

  & .select__control:focus,
  & .select__control--is-focused {
    border: ${getFocusBorder};
    border-right: ${getFocusBorderRight};
    border-bottom: ${getFocusBorderBottom};
    box-shadow: none;
  }

  & .select__control--menu-is-open {
    border-radius: ${getBorderRadiusOnOpen};
  }

  & .select__control:hover {
    border: ${getHoverBorder};
    border-right: ${getHoverBorderRight};
    border-bottom: ${getHoverBorderBottom};
    box-shadow: none;
  }

  & .select__dropdown-indicator {
    padding: ${getDropdownIndicatorPadding};
  }

  & .select__indicator-separator {
    display: none;
  }

  & .select__placeholder {
    color: ${getPlaceholderColor};
  }

  & .select__single-value {
    color: ${getSingleValueColor};
  }

  & .select__menu {
    border: ${getMenuBorder};
    color: ${getMenuColor};
    margin-top: ${getMenuMarginTop};
    box-shadow: none;
    border-radius: ${getMenuBorderRadius};
    border-top: ${getMenuBorderTop};
    padding: ${getMenuPadding};
  }

  & .select__menu-list {
    max-height: 248px;
    min-height: 40px;
    background-color: ${getMenuListBackgroundColor};
    box-shadow: ${getMenuListBoxShadow};
    border-radius: ${getMenuListBorderRadius};
    padding: 0;
  }

  & .select__option {
    height: ${props => (props.autoOptionHeight ? 'auto' : '30px')};
    padding: ${getOptionPadding};
    color: ${getOptionColor};
    cursor: pointer;
    font-size: ${getFontSize};
    font-size: ${props => getFontSize(props, 'mobile')};
    font-family: ${getOptionFontFamily};
    line-height: ${getLineHeight};

    ${breakpoint('portrait')`
      font-size: ${props => getFontSize(props, 'portrait')};
    `};
  }

  & .select__option--is-focused {
    background-color: ${getFocusedOptionBackground} !important;
    border: ${getFocusedOptionBorder} !important;
    color: ${getFocusedOptionColor} !important;
  }

  & .select__option--is-selected {
    position: relative;
    background-color: inherit;
    border: inherit;
    color: ${getOptionColor};
    font-family: ${getSelectedOptionFontFamily};
  }

  & .select__option--is-selected:before {
    display: ${props => (isCheckIconVisible(props) ? 'block' : 'none')};
    left: 0;
    content: url(${config.url}/assets/${getCheckIcon});
    position: absolute;
    margin-left: 16px;
    margin-right: 12px;
    margin-top: 2px;
    width: 16px;
    height: 16px;
  }

  & .select__value-container {
    padding: 0;
  }

  & input[type='number']::-webkit-outer-spin-button,
  & input[type='number']::-webkit-inner-spin-button {
    appearance: none;
  }

  & input[type='number'] {
    appearance: textfield;
  }
`

export default InputSelectView
